import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Input } from "../../../ui/input";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog';
import { getPorts, PortApi, deletePort } from "../../../services/portService";
import { mapTypeFromApi } from "../../../utils/portTypeMapper";
import { useScreenPermission } from "../../../hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";


export const PortMasterListPage = () => {
  const { setPageHeader } = usePageHeader();
  const navigate = useNavigate();
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [portToDelete, setPortToDelete] = useState<string | null>(null);
  
  const [sortField, setSortField] = useState<
    "portName" | "portShortName" | null
  >(null);

  const deletingPort = ports.find((p) => p.publicId === portToDelete);

  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

  const loadPorts = async () => {
    try {
      setLoading(true);
      const res = await getPorts({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      setPorts(res.items);
      setTotalCount(res.totalCount);
    } catch (error) {
      console.error("Failed to load ports", error);
      toast.error("Failed to load ports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    loadPorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, pageNo, pageSize]);

  const handleDelete = (publicId: string) => {
    setPortToDelete(publicId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!portToDelete) return;

    try {
      await deletePort(portToDelete);

      toast.success("Port deleted successfully");

      setDeleteDialogOpen(false);
      setPortToDelete(null);

      loadPorts();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete port");
    }
  };

  const handleSort = (field: "portName" | "portShortName") => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedPorts = [...ports].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;

    const aValue = sortField === "portName" ? a.portName : a.portShortName;
    const bValue = sortField === "portName" ? b.portName : b.portShortName;

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;

    return 0;
  });

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by port name or short name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => navigate("/port-master/add")}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Port
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ marginTop: "10px", gap: "0px" }}>
          <CardContent style={{ padding: "0px 0px 10px" }}>
            <div className="lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5", padding: "1px 16px" }}
                      className="cursor-pointer"
                      onClick={() => handleSort("portName")}
                    >
                      <div className="flex items-center gap-1">
                        Port Name
                        {sortField === "portName" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    <TableHead style={{ color: "#5B9BD5", padding: "1px 16px" }}>
                      Port Name (Arabic)
                    </TableHead>

                    <TableHead
                      style={{ color: "#5B9BD5", padding: "1px 16px" }}
                      onClick={() => handleSort("portShortName")}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-1">
                        Short Name
                        {sortField === "portShortName" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead style={{ color: "#5B9BD5", padding: "1px 16px" }}>
                      Type
                    </TableHead>
                    <TableHead style={{ color: "#5B9BD5", padding: "1px 16px" }}>
                      Status
                    </TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{ color: "#5B9BD5", padding: "1px 16px" }}
                        className="text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedPorts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        No ports found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedPorts.map((port) => (
                      <TableRow
                        key={port.publicId}
                        className="hover:bg-sky-50/30"
                      >
                        <TableCell style={{ padding: "8px 16px" }}>
                          {port.portName}
                        </TableCell>

                        <TableCell dir="rtl" style={{ padding: "8px 16px" }}>
                          {port.portNameArabic}
                        </TableCell>

                        <TableCell style={{ padding: "8px 16px" }}>
                          {port.portShortName}
                        </TableCell>
                        <TableCell style={{ padding: "8px 16px" }}>
    {mapTypeFromApi(port.portType)}
  </TableCell>

                        <TableCell style={{ padding: "8px 16px" }}>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              port.isActive
                                ? 'bg-green-100 text-green-700 border-green-200'
                                : 'bg-gray-100 text-gray-700 border-gray-200'
                            } text-xs `}
                          >
                            {port.isActive ? "Active" : "Inactive"}
                          </span>
                        </TableCell>

                        <TableCell
                          className="text-right"
                          style={{ padding: "8px 16px" }} 
                        >
                          <div className="flex gap-1 justify-end">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  navigate(`/port-master/${port.publicId}`)
                                }
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(port.publicId)}
                                className="text-red-600"
                                style={{ padding: "0px" }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="ports"
          />
        </Card>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Port
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingPort?.portName}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setPortToDelete(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
